import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { AxiosError } from "axios";
import {
  getWorkshops,
  createWorkshop,
  updateWorkshop,
  deleteWorkshop,
  addWorkshopParticipant,
  removeWorkshopParticipant,
  getPendingFeedbackWorkshopInstances,
} from "../../api/workshops";
import { CreateWorkshopDto, UpdateWorkshopDto } from "../../types/workshopTypes";
import { authReducerActionType } from "../../types/globalTypes";
import { showErrorMessage, showSuccessMessage } from "../../utils/toastUtils";

/**
 * Hook that handles the workshops api calls, uses react-query
 * @returns The workshops, a function to get workshop details, and functions to create, update, and delete workshops
 */
const useWorkshops = () => {
  const { auth, dispatchAuth } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // ############
  // GET QUERIES
  // ############

  // getWorkshops query
  const {
    data: workshopsData,
    isLoading: isWorkshopsLoading,
    isError: isWorkshopsError,
  } = useQuery({
    queryKey: ["workshops"],
    queryFn: getWorkshops,
  });

  const workshops = workshopsData?.data || [];

  //getPendingFeedbackWorkshopInstances query
  const { data: pendingFeedbackForWorkshopInstancesData } = useQuery({
    queryKey: ["pendingFeedbackWorkshopInstances"],
    queryFn: getPendingFeedbackWorkshopInstances,
  });

  const pendingFeedbackForWorkshopInstances = pendingFeedbackForWorkshopInstancesData?.data || [];

  // ----------------------------------------------------------------------------------
  // ############
  // CREATE QUERIES
  // ############

  // createWorkshop mutation
  const { mutateAsync: mutateCreateWorkshop } = useMutation({
    mutationFn: createWorkshop,
    onError: (err: AxiosError) => {
      showErrorMessage("Workshop erstellen fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["workshops"] }); // Invalidate the workshops data to refetch the data
      showSuccessMessage("Workshop erfolgreich erstellt");
    },
  });

  /**
   * Function to create a new workshop
   * @param workshopData The workshop data to create
   */
  const createWorkshopHandler = async (workshopData: CreateWorkshopDto) => {
    return await mutateCreateWorkshop(workshopData);
  };

  // ----------------------------------------------------------------------------------
  // ############
  // UPDATE QUERIES
  // ############

  // updateWorkshop mutation
  const { mutateAsync: mutateUpdateWorkshop } = useMutation({
    mutationFn: ({ workshopId, workshopData }: { workshopId: number; workshopData: UpdateWorkshopDto }) =>
      updateWorkshop(workshopId, workshopData),
    onError: (err: AxiosError) => {
      showErrorMessage("Workshop aktualisieren fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
    onSuccess: async (_, { workshopId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["workshops"] }), // Invalidate the workshops data to refetch the data
        queryClient.invalidateQueries({ queryKey: ["workshopDetails", workshopId] }), // Invalidate the specific workshop data
      ]);
      showSuccessMessage("Workshop erfolgreich aktualisiert");
    },
  });

  /**
   * Function to update an existing workshop
   * @param workshopId The ID of the workshop to update
   * @param workshopData The workshop data to update
   */
  const updateWorkshopHandler = async (workshopId: number, workshopData: UpdateWorkshopDto) => {
    return await mutateUpdateWorkshop({ workshopId, workshopData });
  };

  const { mutateAsync: mutateRegisterForWorkshopInstance } = useMutation({
    mutationFn: ({ workshopId, workshopInstanceId }: { workshopId: number; workshopInstanceId: number }) =>
      addWorkshopParticipant(workshopId, workshopInstanceId, auth.userID ? auth.userID : -1),
    onError: (err: AxiosError) => {
      showErrorMessage("Workshop Anmeldung fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
    onSuccess: (_, { workshopInstanceId }) => {
      queryClient.invalidateQueries({ queryKey: ["workshopInstanceDetails", workshopInstanceId] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["userEvents"] });
      showSuccessMessage("Du wurdest für den Workshop angemeldet");
    },
  });

  /**
   * Function to register the current user for a workshop instance
   * @param workshopId The ID of the workshop
   * @param workshopInstanceId The ID of the workshop instance
   */
  const registerForWorkshopInstance = async (workshopId: number, workshopInstanceId: number) => {
    return await mutateRegisterForWorkshopInstance({ workshopId, workshopInstanceId });
  };

  // ----------------------------------------------------------------------------------
  // ############
  // DELETE QUERIES
  // ############

  // deleteWorkshop mutation
  const { mutateAsync: mutateDeleteWorkshop } = useMutation({
    mutationFn: deleteWorkshop,
    onError: (err: AxiosError) => {
      if (err.response?.status === 409) {
        showErrorMessage("Workshop löschen fehlgeschlagen: Workshop hat Instanzen");
      } else {
        showErrorMessage("Workshop löschen fehlgeschlagen");
        if (err.response?.status === 401) {
          dispatchAuth({ type: authReducerActionType.deauthenticate });
        }
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["workshops"] }); // Invalidate the workshops data to refetch the data
      showSuccessMessage("Workshop erfolgreich gelöscht");
      // Redirect to workshops overview after successful deletion
      navigate("/workshops");
    },
  });

  /**
   * Function to delete a workshop
   * @param workshopId The ID of the workshop to delete
   */
  const deleteWorkshopHandler = async (workshopId: number) => {
    return await mutateDeleteWorkshop(workshopId);
  };

  const { mutateAsync: mutateRemoveFromWorkshopInstance } = useMutation({
    mutationFn: ({ workshopId, workshopInstanceId }: { workshopId: number; workshopInstanceId: number }) =>
      removeWorkshopParticipant(workshopId, workshopInstanceId, auth.userID ? auth.userID : -1),
    onError: (err: AxiosError) => {
      showErrorMessage("Workshop Abmeldung fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
    onSuccess: (_, { workshopInstanceId }) => {
      queryClient.invalidateQueries({ queryKey: ["workshopInstanceDetails", workshopInstanceId] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["userEvents"] });
      showSuccessMessage("Du wurdest von dem Workshop abgemeldet");
    },
  });

  /**
   * Function to remove the current user from a workshop instance
   * @param workshopId The ID of the workshop
   * @param workshopInstanceId The ID of the workshop instance
   */
  const deregisterFromWorkshopInstance = async (workshopId: number, workshopInstanceId: number) => {
    return await mutateRemoveFromWorkshopInstance({ workshopId, workshopInstanceId });
  };

  return {
    // Data
    workshops,
    isWorkshopsLoading,
    isWorkshopsError,
    pendingFeedbackForWorkshopInstances,
    // Functions
    createWorkshop: createWorkshopHandler,
    updateWorkshop: updateWorkshopHandler,
    deleteWorkshop: deleteWorkshopHandler,
    registerForWorkshopInstance,
    deregisterFromWorkshopInstance,
  };
};

export default useWorkshops;
